import { youtubeSay, npcLineFeature } from "@greg-chick/shared";
import { useEffect, useState } from "react";
import {
  getProp,
  getObjectZ,
  type AssetSpec,
  type ZOrderOp,
  type TiledObject,
} from "@greg-chick/shared";
import type { Manifest, ManifestEntry } from "../lib/contentApi";
import { PropFilePicker } from "./PropFilePicker";
import {
  OBJ_TYPES,
  TYPE_META,
  COLOR,
  typeLabel,
  IMG_TABS,
  zoneImageOptions,
  type ImgOpt,
  type ImgTabId,
} from "./zoneEditorMeta";

/**
 * 존 편집기의 **오른쪽 속성 패널** — 고른 오브젝트 하나를 고치는 폼과, 그림을 고르는 피커.
 *
 * ZoneEditor 가 783줄이라 "캔버스에 끌어다 놓기"와 "고른 것의 속성 고치기"가
 * 한 파일에 섞여 있었다(docs/build/20 B5). 캔버스와 패널은 서로 `map`·`onChange` 로만
 * 이야기하므로 잎(leaf) 쪽인 패널을 떼어냈다.
 */

// ── 클립(장식·NPC) 피커 ─────────────────────────────────
// '움직임 만들기'·'그림 넣기'에 등록된 장식(decor.*)·NPC(npc.*)를 존 편집에서 골라 배치.
// 애니(움직이는 것)는 🎞️ 로 표시하고 썸네일에서 재생 → 무엇을 넣는지 눈으로 확인.
export interface ClipOpt { id: string; url: string; anim: boolean; stripUrl?: string }

export function collectClips(assets: Manifest["assets"], kind: "decor" | "npc"): ClipOpt[] {
  const re = kind === "decor" ? /^decor\.([a-z0-9_]+)(\.strip)?$/ : /^npc\.([a-z0-9_]+)(\.(?:down|up|side))?$/;
  const map = new Map<string, { base?: string; strip?: string; anim: boolean }>();
  for (const [k, e] of Object.entries(assets)) {
    if (!e?.url) continue;
    const m = re.exec(k);
    if (!m) continue;
    const id = m[1]!;
    const cur = map.get(id) ?? { anim: false };
    if (m[2]) { cur.anim = true; if (!cur.strip) cur.strip = e.url; }
    else cur.base = e.url;
    map.set(id, cur);
  }
  return [...map]
    .map(([id, v]) => ({ id, url: v.base ?? v.strip ?? "", anim: v.anim, stripUrl: v.strip }))
    .filter((o) => o.url)
    .sort((a, b) => Number(b.anim) - Number(a.anim) || a.id.localeCompare(b.id)); // 움직이는 것 먼저
}

/** 클립 썸네일 — 애니면 스트립을 재생(2fps), 아니면 정지 이미지. */
export function ClipThumb({ opt }: { opt: ClipOpt }) {
  const animUrl = opt.anim ? opt.stripUrl : undefined;
  const [n, setN] = useState(1);
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!animUrl) return;
    let alive = true;
    const img = new Image();
    img.onload = () => alive && setN(Math.max(1, Math.round(img.width / img.height)));
    img.src = animUrl;
    return () => { alive = false; };
  }, [animUrl]);
  useEffect(() => {
    if (!animUrl || n < 2) return;
    const t = window.setInterval(() => setI((v) => (v + 1) % n), 500);
    return () => window.clearInterval(t);
  }, [animUrl, n]);
  if (animUrl) {
    return (
      <span
        className="clips__thumb clips__thumb--anim"
        style={{
          backgroundImage: `url(${animUrl})`,
          backgroundSize: `${n * 100}% 100%`,
          backgroundPosition: n > 1 ? `${(i / (n - 1)) * 100}% 0` : "0 0",
        }}
      />
    );
  }
  return <span className="clips__thumb"><img src={opt.url} alt="" /></span>;
}

export function ClipPicker({ clips, value, onPick, emptyHint }: {
  clips: ClipOpt[];
  value: string;
  onPick: (id: string) => void;
  emptyHint: string;
}) {
  if (!clips.length) return <p className="zone__tip">{emptyHint}</p>;
  return (
    <div className="clips">
      {clips.map((c) => (
        <button
          type="button"
          key={c.id}
          className={`clips__opt${value === c.id ? " on" : ""}`}
          title={c.id}
          onClick={() => onPick(c.id)}
        >
          <ClipThumb opt={c} />
          {c.anim ? <span className="clips__badge" title="움직이는 클립">🎞️</span> : null}
          <span className="clips__name">{c.id}</span>
        </button>
      ))}
    </div>
  );
}

export function ObjectPanel({
  obj,
  images,
  imgUrl,
  decorClips,
  npcClips,
  cardImgClips,
  propSpec,
  onImageAdded,
  onType,
  onProp,
  onReorder,
  onDelete,
  label,
}: {
  obj: TiledObject;
  /** 머리에 쓸 이름 — 이름이 겹치면 자리가 붙은 것(zoneEditorMeta.objectLabel · 캔버스와 같은 값) */
  label?: string;
  images: ImgOpt[];
  imgUrl: (key: string | undefined) => string | undefined;
  decorClips: ClipOpt[];
  npcClips: ClipOpt[];
  cardImgClips: ClipOpt[];
  /** 소품 업로드 규격(content/asset-slots.json 의 `decor`). 없으면 올리기 칸을 감춘다. */
  propSpec?: AssetSpec;
  /** 새 소품을 올렸을 때 — 호출부가 매니페스트에 넣고 저장 바에 쌓는다. */
  onImageAdded?: (key: string, entry: ManifestEntry) => void;
  onType: (t: string) => void;
  onProp: (name: string, value: string) => void;
  onReorder: (op: ZOrderOp) => void;
  onDelete: () => void;
}) {
  const v = (n: string) => String(getProp(obj, n) ?? "");
  const sprite = v("sprite");
  const scale = Number(getProp(obj, "scale") ?? 1) || 1;
  const rot = Number(getProp(obj, "rot") ?? 0) || 0;
  const flipx = getProp(obj, "flipx") === "1" || getProp(obj, "flipx") === true;
  const flipy = getProp(obj, "flipy") === "1" || getProp(obj, "flipy") === true;
  const z = getObjectZ(obj);
  const [picking, setPicking] = useState(false);
  const [q, setQ] = useState("");
  // 기본은 **소품** 탭 — 예전엔 전체 262개를 앞 60개만 그려서 소품이 한 개도 안 보였다.
  const [tab, setTab] = useState<ImgTabId>("decor");
  const all = zoneImageOptions(images, tab, q);
  const SHOWN = 60;
  const opts = all.slice(0, SHOWN);

  return (
    <div className="panel">
      <div className="panel__head">
        <b>
          <span className="zone__chip-dot" style={{ background: COLOR[obj.type ?? ""] ?? "#888" }} aria-hidden="true" />
          {typeLabel(obj.type)} <span className="panel__name">· {label ?? String(getProp(obj, "title_ko") || obj.name)}</span>
        </b>
        <button type="button" className="panel__del" onClick={onDelete}>삭제</button>
      </div>
      <label>종류
        <select value={obj.type ?? ""} onChange={(e) => onType(e.target.value)}>
          {OBJ_TYPES.map((t) => <option key={t} value={t}>{TYPE_META[t]?.icon} {typeLabel(t)}</option>)}
        </select>
      </label>

      {/* 이미지 + 변환(모든 타입 공통) */}
      <div className="objimg">
        <div className="objimg__cur">
          <div className="objimg__thumb">
            {imgUrl(sprite || undefined) ? (
              <img src={imgUrl(sprite || undefined)} alt="" />
            ) : (
              /* 그림을 안 고르면 아이 화면에는 **동그란 빛**이 뜬다(마을 선택지와 같은 모습).
                 예전엔 그냥 "없음" 이라고만 해서, 운영자는 아무것도 안 나오는 줄 알았다.
                 친구(NPC)는 예외 — 빛만 남기면 누가 있는지 알 수 없어 예전 모습 그대로다. */
              <span className="objimg__none">{v("type") === "npc" ? "없음" : "✨ 빛"}</span>
            )}
          </div>
          <div className="objimg__btns">
            <button type="button" className="btn" onClick={() => setPicking((p) => !p)}>이미지 선택</button>
            {sprite ? <button type="button" className="btn" onClick={() => onProp("sprite", "")}>이미지 없음</button> : null}
          </div>
        </div>
        {picking ? (
          <div className="objimg__pick">
            {/* 종류별 탭 — 기본은 소품. 「전체」는 도피처지만 거기서도 소품이 앞에 온다. */}
            <div className="objimg__tabs" role="tablist">
              {IMG_TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  data-on={tab === t.id || undefined}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <input className="objimg__search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="이름으로 찾기…" />

            {/* 내 그림 올리기 — **파일 먼저, 이름은 자동**. 올린 것은 소품(decor)이 되어
                [➕ 직접 만들기] 목록에도 같이 나타난다. */}
            {propSpec && onImageAdded ? (
              <div className="objimg__add">
                <PropFilePicker
                  spec={propSpec}
                  takenKeys={images.map((i) => i.key)}
                  onAdded={onImageAdded}
                  onPickOne={(key) => {
                    onProp("sprite", key); // 한 장만 올렸으면 바로 붙인다
                    setPicking(false);
                  }}
                />
              </div>
            ) : null}

            <div className="objimg__grid">
              {all.length === 0 ? (
                <p className="zone__tip">
                  {q.trim() ? "찾는 그림이 없어요." : "이 종류의 그림이 아직 없어요 — 위에서 올려 보세요."}
                </p>
              ) : null}
              {opts.slice(0, 60).map((i) => (
                <button
                  key={i.key}
                  type="button"
                  className={`objimg__opt${sprite === i.key ? " on" : ""}`}
                  title={i.key}
                  onClick={() => {
                    onProp("sprite", i.key);
                    setPicking(false);
                  }}
                >
                  <img src={i.url} alt="" />
                  <span>{i.key}</span>
                </button>
              ))}
            </div>
            {/* 말없이 자르지 않는다 — 예전엔 60개에서 잘린 걸 알 방법이 없었다 */}
            {all.length > SHOWN ? (
              <p className="zone__tip">{all.length}개 중 {SHOWN}개만 보여요 — 이름으로 찾아 보세요.</p>
            ) : null}
          </div>
        ) : null}
      </div>

      {sprite ? (
        <div className="objxform">
          <label className="objxform__row">크기 {scale.toFixed(1)}×
            <input type="range" min={0.2} max={4} step={0.1} value={scale} onChange={(e) => onProp("scale", e.target.value)} />
          </label>
          <label className="objxform__row">회전 {rot}°
            <input type="range" min={-180} max={180} step={5} value={rot} onChange={(e) => onProp("rot", e.target.value)} />
          </label>
          <div className="objxform__flips">
            <button type="button" className={flipx ? "on" : ""} onClick={() => onProp("flipx", flipx ? "" : "1")}>↔ 좌우반전</button>
            <button type="button" className={flipy ? "on" : ""} onClick={() => onProp("flipy", flipy ? "" : "1")}>↕ 상하반전</button>
            <button type="button" onClick={() => { onProp("scale", "1"); onProp("rot", "0"); onProp("flipx", ""); onProp("flipy", ""); }}>초기화</button>
          </div>
          <p className="zone__tip">위치는 캔버스에서 <b>드래그</b>로 옮기세요.</p>
        </div>
      ) : null}

      {/* 앞/뒤 순서(z 깊이) — 겹치는 오브젝트 우선순위. 이미지 없는 오브젝트도 포함해 항상 노출. */}
      <div className="objz">
        <span className="objz__label">앞뒤 순서 {z !== 0 ? <em className="objz__val">(z {z > 0 ? "+" : ""}{z})</em> : <em className="objz__val objz__val--auto">(자동)</em>}</span>
        <div className="objz__btns">
          <button type="button" onClick={() => onReorder("front")} title="다른 것들보다 맨 앞으로">⤒ 맨 앞</button>
          <button type="button" onClick={() => onReorder("forward")} title="한 단계 앞으로">↑ 앞으로</button>
          <button type="button" onClick={() => onReorder("backward")} title="한 단계 뒤로">↓ 뒤로</button>
          <button type="button" onClick={() => onReorder("back")} title="다른 것들보다 맨 뒤로">⤓ 맨 뒤</button>
        </div>
        {z !== 0 ? (
          <button type="button" className="objz__reset" onClick={() => onProp("z", "")}>순서 자동(초기화)</button>
        ) : null}
        <p className="zone__tip">겹칠 때 무엇이 위에 보일지 정해요. ‘자동’은 아래쪽에 있을수록 앞(원근).</p>
      </div>

      <label>라벨(title_ko)
        <input value={v("title_ko")} onChange={(e) => onProp("title_ko", e.target.value)} />
      </label>
      <label className="zone__solid">
        <input
          type="checkbox"
          checked={v("solid") === "1" || getProp(obj, "solid") === true}
          onChange={(e) => onProp("solid", e.target.checked ? "1" : "")}
        />
        통과 막기(아바타가 못 지나감)
      </label>
      {obj.type === "portal" ? (
        <>
          <label>이동 존(to)
            <input value={v("to")} placeholder="lobby/gyeongju/…" onChange={(e) => onProp("to", e.target.value)} />
          </label>
          {/* 🚪 문이 열리는 거리 — 예전엔 이 칸이 없어 경주 문(64)은 손으로 넣은 값이었다. 벽에 붙은 문은 48~64 가 맞다(2026-09-29 무대 옆문) */}
          <label>문이 열리는 거리(px)
            <input
              type="number"
              min={12}
              max={96}
              value={v("trigger")}
              placeholder="24"
              onChange={(e) => onProp("trigger", e.target.value)}
            />
            <em className="hint">아이가 이만큼 가까이 오면 들어가요. 비우면 24(반 칸). 벽에 붙은 문은 48~64.</em>
          </label>
        </>
      ) : null}
      {obj.type === "link" || obj.type === "store" ? (
        <label>URL
          <input value={v("url")} onChange={(e) => onProp("url", e.target.value)} />
        </label>
      ) : null}
      {obj.type === "video" ? (
        <label>유튜브
          <input value={v("video_youtube")} onChange={(e) => onProp("video_youtube", e.target.value)} />
          {/* 아이 화면이 쓰는 그 판정으로 본다 — 여기서 통과시킨 주소가 「영상 준비중이에요」가 되면 안 된다 */}
          {youtubeSay(v("video_youtube")) ? <em className="warn">{youtubeSay(v("video_youtube"))}</em> : null}
        </label>
      ) : null}
      {/**
        * 🔴 **카드에는 영상이 안 나온다.**
        *
        * 예전에는 `card` 에도 이 칸을 보여 줬는데, 아이 화면의 `Card` 는 삽화와 낭독만 그린다
        * (`WorldOverlay.tsx` — 영상은 `type: video` 만). 그래서 운영자가 정성껏 넣은 주소가
        * **아무 데도 안 쓰이고** 있었다(실제로 다섯 곳에 남아 있다).
        *
        * 빈 카드에는 칸을 아예 안 보여 준다. 이미 값이 있으면 **지울 수 있게** 두고 왜 그런지 말한다 —
        * 안 보여 주면 못 지우고, 다음 사람이 또 「왜 영상이 안 나오지」를 겪는다.
        */}
      {obj.type === "card" && v("video_youtube") ? (
        <label>유튜브 <em className="warn">카드에는 영상이 안 나와요</em>
          <input value={v("video_youtube")} onChange={(e) => onProp("video_youtube", e.target.value)} />
          <em className="hint">
            지우거나, 영상을 보여 주려면 오브젝트 종류를 <b>video</b> 로 바꾸세요.
          </em>
        </label>
      ) : null}
      {obj.type === "npc" ? (
        <>
          <label className="clips__lbl">캐릭터 고르기 <em className="clips__hint">🎞️ = 걷기 애니 있음</em></label>
          <ClipPicker
            clips={npcClips}
            value={v("npc_sprite")}
            onPick={(id) => onProp("npc_sprite", id)}
            emptyHint="아직 NPC가 없어요. ‘그림 넣기 → 캐릭터’나 ‘움직임 만들기’에서 만든 뒤 게시하세요."
          />
          <label>직접 입력(npc_sprite)
            <input
              value={v("npc_sprite")}
              list="npc-sprite-list"
              placeholder="greg_cat / 커스텀 NPC 이름"
              onChange={(e) => onProp("npc_sprite", e.target.value)}
            />
            <datalist id="npc-sprite-list">
              <option value="greg_cat" /><option value="greg_white" /><option value="chick" />
            </datalist>
          </label>
          <label>향함(npc_face)
            <select value={v("npc_face") || "down"} onChange={(e) => onProp("npc_face", e.target.value)}>
              <option value="down">앞(정면)</option>
              <option value="up">뒤</option>
              <option value="left">왼쪽</option>
              <option value="right">오른쪽</option>
            </select>
          </label>
          <label>대사(| 로 구분)
            <input value={v("npc_lines")} onChange={(e) => onProp("npc_lines", e.target.value)} />
            {/* 🗣️ 아이 화면과 같은 판정(shared npcLineFeature) — 숨긴 곳을 가리키는 말은 그 모드에서 빠진다.
                말없이 빠지면 운영자는 「왜 병아리가 조용하지」만 겪는다. */}
            {(() => {
              const hidden = v("npc_lines")
                .split("|")
                .map((s) => s.trim())
                .filter((s) => s && npcLineFeature(s));
              return hidden.length ? (
                <em className="hint">
                  🧸 영유아·수업 모드에서는 마켓을 숨겨서 이 말은 안 나와요: 「{hidden.join("」 「")}」
                </em>
              ) : null;
            })()}
          </label>
        </>
      ) : null}
      {obj.type === "card" ? (
        <>
          <label>설명(아이)
            <input value={v("desc_child_ko")} onChange={(e) => onProp("desc_child_ko", e.target.value)} />
          </label>
          {/* 카드 팝업 안에 뜨는 그림. 여기 없으면 맵 JSON 을 직접 고쳐야 했다(누락 보완).
              그림 자체를 갈아끼우는 건 '🏞️ 체험존 그림' 탭이 더 쉽다 — 여기선 어느 그림을 쓸지만 고른다. */}
          <label className="clips__lbl">눌렀을 때 보여줄 그림</label>
          <ClipPicker
            clips={cardImgClips}
            value={v("card_image")}
            onPick={(id) => onProp("card_image", id)}
            emptyHint="아직 올린 그림이 없어요. ‘🏞️ 체험존 그림’이나 ‘그림 넣기’에서 올린 뒤 게시하세요."
          />
          <label>직접 입력(card_image)
            <input value={v("card_image")} placeholder="ar.poster.ar_chumseongdae" onChange={(e) => onProp("card_image", e.target.value)} />
          </label>
        </>
      ) : null}
      {obj.type === "decor" ? (
        <>
          <label className="clips__lbl">장식 고르기 <em className="clips__hint">🎞️ = 움직이는 클립</em></label>
          <ClipPicker
            clips={decorClips}
            value={v("decor_sprite")}
            onPick={(id) => onProp("decor_sprite", id)}
            emptyHint="아직 장식이 없어요. ‘그림 넣기 → 장식’이나 ‘움직임 만들기’에서 만든 뒤 게시하세요."
          />
          <label>직접 입력(decor_sprite)
            <input value={v("decor_sprite")} placeholder="hanok / observatory / tree …" onChange={(e) => onProp("decor_sprite", e.target.value)} />
          </label>
          <label>크기(decor_size, px)
            <input value={v("decor_size")} placeholder="96" onChange={(e) => onProp("decor_size", e.target.value)} />
          </label>
        </>
      ) : null}
    </div>
  );
}
